import chroma from 'chroma-js';
import classNames from 'classnames';
import React from 'react';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import SelectReact from 'react-select';
import makeAnimated from 'react-select/animated';
import { Tooltip } from 'rizzui';

type SelectProps = {
    id: string;
    label: string;
    info?: string;
    error?: any;
    isSearchable?: boolean;
    isMulti?: boolean;
    isClearable?: boolean;
    isDisabled?: boolean;
    options?: any;
    onChange?: any;
    handle?: any;
    value?: any;
    defaultValue?: any;
    placeholder?: string;
    noOption?: any;
    containerClassName?: string;
    className?: string;
    onBlur?: any;
    display?: boolean;
    required?: boolean;
    isLoading?: boolean;
    onInputChange?: any;
    inputValue?: any;
    key?: any;
    customPlaceholder?: string;
    italic?: boolean;
    labelClassName?: string;
};

const CustomSelect: React.FC<SelectProps> = ({ customPlaceholder, id, label, info, isSearchable, isMulti, isClearable, isDisabled, required, options, onChange, handle, value, defaultValue, placeholder, noOption, containerClassName, className, onBlur, display = true, error, isLoading = false, onInputChange, inputValue, key, italic = false, labelClassName }) => {
    return (
        <div className={classNames(containerClassName, { 'hidden': !display })} key={key}>
            <label className='block'>
                <span className={`rizzui-input-label block text-sm mb-1.5 font-medium ${labelClassName}`}>
                    {label}
                    {required && <span className="text-red-500"> *</span>}
                    {info && (
                        <Tooltip
                            size="md"
                            content={<div className='tooltip-container'>{info}</div>}
                            placement="top"
                            color="invert"
                        >
                            <div className={'inline-block ms-1'}>
                                <HiOutlineInformationCircle size={20} color={'primary'} />
                            </div>
                        </Tooltip>
                    )}
                </span>
                <SelectReact
                    key={key}
                    id={id}
                    name={id}
                    tabSelectsValue
                    components={makeAnimated()}
                    isSearchable={isSearchable}
                    isMulti={isMulti}
                    isLoading={isLoading}
                    loadingMessage={() => 'Cargando...'}
                    isClearable={isClearable}
                    isDisabled={isDisabled}
                    options={options}
                    onInputChange={onInputChange}
                    onChange={onChange ? ((selectedOption: any) => { onChange(selectedOption); }) : handle}
                    value={value}
                    inputValue={inputValue}
                    defaultValue={defaultValue}
                    onBlur={onBlur}
                    placeholder={`${customPlaceholder ? customPlaceholder : `Elegir ${placeholder || ''}`}...`}
                    noOptionsMessage={noOption || (() => 'No se ha encontrado ningún resultado')}
                    className={className}
                    styles={{
                        menu: (base: any) => ({
                            ...base,
                            zIndex: 15,
                        }),
                        singleValue: (base: any) => ({
                            ...base,
                            fontStyle: italic ? 'italic' : 'normal',
                        }),
                        control: (base: any, state: any) => ({
                            ...base,
                            border: error ? '1.5px solid rgb(255 0 0)' : '1px solid rgb(229 231 235)',
                            borderRadius: '0.375rem',
                            backgroundColor: isDisabled ? '#e9ecef' : 'transparent',
                            '&:hover': {
                                borderColor: 'rgb(54, 135, 43)',
                            },
                            '&:focus': {
                                borderColor: 'rgb(54, 135, 43)',
                                boxShadow: '0 0 0 2px rgb(54, 135, 43)',
                            },
                        }),
                        option: (provided: any, state: any) => ({
                            ...provided,
                            backgroundColor: state.isFocused ? 'rgb(54, 135, 43)' : 'white',
                            fontStyle: italic ? 'italic' : 'normal',
                            color: state.isFocused ? 'white' : 'black',
                            cursor: state.data.disabled ? 'not-allowed' : 'pointer',
                            opacity: state.data.disabled ? 0.5 : 1,
                            pointerEvents: state.data.disabled ? 'none' : 'auto',
                            '&:hover': {
                                backgroundColor: 'rgb(54, 135, 43)',
                                color: 'white',
                                borderColor: '#000000 !important'
                            }
                        }),
                        multiValue: (styles: any, { data }: any) => {
                            return {
                                ...styles,
                                width: 'fit-content',
                            };
                        }
                        ,
                        multiValueLabel: (styles: any, { data }: any) => {
                            const backgroundColor = data.color || '#E6E5E5';
                            let textColor = 'black';
                            if (chroma.valid(backgroundColor)) {
                                textColor = chroma.contrast(backgroundColor, 'white') > 4.5 ? 'white' : 'black';
                            }
                            return {
                                ...styles,
                                fontStyle: italic ? 'italic' : 'normal',
                                textOverflow: 'unset',
                                backgroundColor,
                                color: textColor,
                            };
                        },
                        multiValueRemove: (styles: any, { data }: any) => {
                            const backgroundColor = data.color || '#E6E5E5';
                            let textColor = 'black';
                            if (chroma.valid(backgroundColor)) {
                                textColor = chroma.contrast(backgroundColor, 'white') > 4.5 ? 'white' : 'black';
                            }
                            return {
                                ...styles,
                                backgroundColor,
                                color: textColor + ' !important',
                                ':hover': {
                                    backgroundColor: chroma.contrast(backgroundColor, 'white') > 4.5 ? chroma(backgroundColor).brighten(0.5).hex() : chroma(backgroundColor).darken(0.5).hex(),
                                    color: textColor,
                                }
                            };
                        },
                    }}
                />
            </label>

            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
        </div>
    );
};

export default CustomSelect;